import { FeedItemDoc, RfiDoc } from 'common';
import { WorkflowState, WorkflowStateComplete } from 'common';
import { User } from '../../global';

/**
 * Pure workflow state navigation and timeline construction logic for RFI documents.
 * No DB communication here — see RfiService for that.
 */
export class RfiWorkflowUtils {
  /**
   * Returns the index of the current active workflow state, accounting for reversion
   * (null state_id entries in history = backward steps, mirroring WorkflowUtils.getRevisionStateIndex).
   */
  static getStateIndex(doc: RfiDoc): number {
    const statusHistory = doc.status_history || [];
    if (statusHistory.length === 0) return 0;

    let lastCompletedStateId: string | null = null;
    let consecutiveNulls = 0;

    for (let i = statusHistory.length - 1; i >= 0; i--) {
      if (statusHistory[i].state_id === null) {
        consecutiveNulls++;
      } else {
        lastCompletedStateId = statusHistory[i].state_id;
        break;
      }
    }

    if (lastCompletedStateId === null) return 0;

    const completedStateIndex = doc.states.findIndex(s => s.id === lastCompletedStateId);
    return Math.max(0, completedStateIndex + 1 - consecutiveNulls);
  }

  /**
   * Returns the index of the last completed state in the states array.
   * Handles null state_id (reversion) gracefully.
   */
  static getLastCompletedStateIdx(doc: RfiDoc): number {
    return RfiWorkflowUtils.getStateIndex(doc) - 1;
  }

  static getCompletedStateId(doc: RfiDoc): string | null {
    const statusHistory = doc.status_history || [];
    if (statusHistory.length === 0) return null;
    const lastStateId = statusHistory[statusHistory.length - 1].state_id;
    return lastStateId ?? null;
  }

  static getStateHistory(doc: RfiDoc): WorkflowStateComplete[] {
    return doc.status_history || [];
  }

  static getPrevState(doc: RfiDoc): WorkflowState {
    const idx = RfiWorkflowUtils.getStateIndex(doc);
    return doc.states[idx - 1] ?? null;
  }

  /**
   * Returns the current (active) workflow state.
   * Null state_id in history means the workflow was reverted to the first state.
   */
  static getCurrentState(doc: RfiDoc): WorkflowState {
    if (!doc.states || !doc.created_date || doc.created_date === '') return null;
    const lastCompletedStateId = RfiWorkflowUtils.getCompletedStateId(doc);
    // Null means reverted to the first state
    if (lastCompletedStateId === null) return doc.states[0] || null;
    const prevIdx = doc.states.findIndex(s => s.id === lastCompletedStateId);
    return prevIdx >= 0 ? doc.states[prevIdx + 1] : null;
  }

  static mustAcceptAnswer(doc: RfiDoc): boolean {
    const idx = RfiWorkflowUtils.getStateIndex(doc);
    return doc.states.length === idx && !doc.accepted_answers.length;
  }

  static getPendingStates(rfi: RfiDoc): WorkflowState[] {
    const index = RfiWorkflowUtils.getStateIndex(rfi);
    return (rfi.states || []).slice(index);
  }

  /**
   * This is client-side only version of completeState.
   * We might need to move this to the server side in the future.
   */
  static completeState(rfi: RfiDoc, date: Date, user: User, state: WorkflowState): RfiDoc {
    rfi.status_history.push({
      state_id: state.id,
      user_id: user.id,
      date: date.toISOString(),
    });
    return rfi;
  }

  static addFeedItem(rfi: RfiDoc, item: FeedItemDoc): RfiDoc {
    return rfi;
  }
}
